import { type FeeVersion, formatBRL, formatBps, formatIsoDate } from '@deizi/shared';

export function versionLabel(v: FeeVersion): string {
  switch (v.state) {
    case 'current':
      return `v${v.number} · vigente desde ${formatIsoDate(v.effectiveFrom)}`;
    case 'past':
      return v.effectiveUntil
        ? `v${v.number} · até ${formatIsoDate(v.effectiveUntil)}`
        : `v${v.number} · encerrada`;
    case 'scheduled':
      return `v${v.number} · agendada para ${formatIsoDate(v.effectiveFrom)}`;
    default:
      return `v${v.number} · rascunho, vale a partir de ${formatIsoDate(v.effectiveFrom)}`;
  }
}

export function versionBadgeClass(v: FeeVersion): string {
  if (v.state === 'current') return 'badge solid';
  if (v.state === 'draft') return 'badge warn';
  if (v.state === 'scheduled') return 'badge info';
  return 'badge';
}

/** Key/value rows describing a version's fees (read-only cards, schedule dialog). */
export function versionRows(
  v: Pick<
    FeeVersion,
    | 'commissionBps'
    | 'paymentFeeBps'
    | 'fixedFees'
    | 'shippingFromCentavos'
    | 'shippingAverageCentavos'
  >,
): [string, string][] {
  const rows: [string, string][] = [['Comissão', formatBps(v.commissionBps)]];
  if (v.fixedFees.length === 0) rows.push(['Tarifa fixa', 'nenhuma']);
  for (const r of v.fixedFees) {
    const label =
      r.toCentavos === null
        ? `Tarifa fixa a partir de ${formatBRL(r.fromCentavos)}`
        : `Tarifa fixa de ${formatBRL(r.fromCentavos)} a ${formatBRL(r.toCentavos)}`;
    rows.push([label, formatBRL(r.feeCentavos)]);
  }
  if (v.shippingAverageCentavos > 0) {
    rows.push([
      v.shippingFromCentavos === null
        ? 'Frete pago por você (média por pedido)'
        : `Parte do frete grátis a partir de ${formatBRL(v.shippingFromCentavos)} (média)`,
      formatBRL(v.shippingAverageCentavos),
    ]);
  } else {
    rows.push(['Frete', 'comprador paga']);
  }
  rows.push(['Taxa de pagamento', formatBps(v.paymentFeeBps)]);
  return rows;
}

export function commissionSummary(v: FeeVersion | null): string {
  if (!v) return '—';
  if (v.commissionBps === 0 && v.paymentFeeBps > 0) return formatBps(v.paymentFeeBps);
  return formatBps(v.commissionBps);
}
